import { useCallback, useEffect, useRef, useState } from "react";

import { SOURCE_VIDAPP } from "@/api";
import { Button, TextArea } from "@/components";
import { EVENT_TYPE_JOURNAL } from "@/const";
import { useDeleteEvent, usePutEvent } from "@/hooks";
import { useTranslate } from "@/providers/context";
import { getDateStringFromDateTime } from "@/utils";

import { useCalendarData } from "../hooks/useCalendarData";

interface JournalInputProps {
  id: string;
  prompt: string;
  date: Date;
}

const useAutoResize = (value: string | undefined) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "auto";
    textarea.style.height = `${Math.max(188, textarea.scrollHeight)}px`;
  }, [value]);

  return textareaRef;
};

export const JournalInput = ({ id, prompt, date }: JournalInputProps) => {
  const { translate } = useTranslate();
  const { mutate: putEventMutate, isLoading: isSaving } = usePutEvent();
  const { mutate: deleteEventMutate, isLoading: isDeleting } = useDeleteEvent();

  const isLoading = isDeleting || isSaving;

  const { getJournalEntriesForDate } = useCalendarData();
  const journalEntries = getJournalEntriesForDate(date);
  const journalEntry = journalEntries.find((e) => e.ItemId === id);

  const [value, setValue] = useState<string | undefined>(
    journalEntry?.EventData,
  );
  const [isEdited, setIsEdited] = useState(false);
  const textareaRef = useAutoResize(value);

  const onButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      textareaRef.current?.focus();
    },
    [textareaRef],
  );

  const handleUndo = useCallback(() => {
    setValue(journalEntry?.EventData ?? "");
    setIsEdited(false);
  }, [journalEntry?.EventData, setValue, setIsEdited]);

  const onSuccess = useCallback(() => {
    setIsEdited(false);
  }, [setIsEdited]);

  const handleSave = useCallback(() => {
    if (isEdited) {
      const payload = {
        ItemId: parseInt(id),
        ItemType: EVENT_TYPE_JOURNAL,
        DataSource: SOURCE_VIDAPP,
        EventType: EVENT_TYPE_JOURNAL,
        EventDate: getDateStringFromDateTime(date),
      };

      if (!value) {
        deleteEventMutate(payload, {
          onSuccess,
        });
      } else {
        putEventMutate(
          {
            ...payload,
            EventData: value,
          },
          {
            onSuccess,
          },
        );
      }
    }
  }, [date, id, value, isEdited, putEventMutate, deleteEventMutate, onSuccess]);

  return (
    <div className="relative mb-7 flex flex-col">
      <div className="p2-semibold mb-4 text-item-primary">{prompt}</div>
      <TextArea
        ref={textareaRef}
        className="min-h-[188px] w-full resize-none pb-[54px]"
        onChange={(e) => {
          setValue(e.target.value);
          setIsEdited(true);
        }}
        value={value}
        placeholder={`${translate("Write here")}...`}
        onBlur={handleSave}
      />
      <div className="absolute bottom-[10px] right-3 flex gap-1">
        <Button
          size="s"
          variant="tertiary"
          className="hover:bg-surface-tertiary"
          onMouseDown={(e) => {
            // Use onMouseDown instead of onClick to prevent the text area from losing focus and triggering the onBlur event
            onButtonClick(e);
            handleUndo();
          }}
          disabled={!isEdited || isLoading}
        >
          {translate("Undo")}
        </Button>
        <Button
          size="s"
          variant="tertiaryHighlight"
          onMouseDown={(e) => {
            onButtonClick(e);
            handleSave();
          }}
          loading={isLoading}
          disabled={!isEdited || isLoading}
        >
          {translate("Save")}
        </Button>
      </div>
    </div>
  );
};
